import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  type BadgeProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@monksflow/monks-ui';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import { useTranslation } from 'react-i18next';

import { getInitials } from '@/shared/utils/getInitials';

interface WorkspaceChipListProps {
  workspaces: WorkspaceResponseDto[];
  variant: Extract<BadgeProps['variant'], 'positive' | 'destructive'>;
  /** Reads above the chips, saying how the list should be understood. */
  label?: string;
}

function WorkspaceAvatar({ workspace }: { workspace: WorkspaceResponseDto }) {
  return (
    <Avatar size="xs">
      <AvatarImage src={workspace.logoUrl ?? undefined} alt="" />
      <AvatarFallback>{getInitials(workspace.name)}</AvatarFallback>
    </Avatar>
  );
}

/**
 * The first workspace as a chip, with the rest behind a "+N" control.
 *
 * A `Popover` rather than a `Tooltip`: the hidden part is a list of arbitrary length that has
 * to scroll, and its trigger is a real button, so it opens from the keyboard and closes on
 * Escape. The MUI original revealed the same names from `onMouseEnter` on a bare `span`, which
 * nobody navigating by keyboard could ever open. An `AvatarGroupCount` was the other candidate
 * and was dropped: it is a presentational `div`, so it would have reproduced that same dead end.
 */
export function WorkspaceChipList({ workspaces, variant, label }: WorkspaceChipListProps) {
  const { t } = useTranslation('admin');

  if (workspaces.length === 0) {
    return null;
  }

  const [first, ...remaining] = workspaces;
  const remainingLabel = t('admin.workspaceChips.otherWorkspaces', {
    remainingCount: remaining.length,
  });

  return (
    <div className="flex flex-col gap-2xs">
      {label && <span className="text-body-sm text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-2xs">
        <Badge
          variant={variant}
          roundness="full"
          size="sm"
          tooltip={first.name}
          className="max-w-37.5"
        >
          <WorkspaceAvatar workspace={first} />
          <span className="min-w-0 truncate">{first.name}</span>
        </Badge>

        {remaining.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              {/* Opening the list must not also open the row it sits in. */}
              <button
                type="button"
                aria-label={remainingLabel}
                className="flex items-center rounded-full outline-none focus-visible:focus-ring"
                onClick={(event) => event.stopPropagation()}
              >
                <Badge variant={variant} roundness="full" size="sm">
                  {`+${String(remaining.length)}`}
                </Badge>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="max-h-60 w-64 overflow-y-auto p-xs"
              onClick={(event) => event.stopPropagation()}
            >
              <p className="p-2xs text-body-sm text-muted-foreground">{remainingLabel}</p>
              <ul className="flex flex-col gap-2xs">
                {remaining.map((workspace) => (
                  <li key={workspace.id} className="flex items-center gap-xs p-2xs">
                    <WorkspaceAvatar workspace={workspace} />
                    <span className="text-body-md">{workspace.name}</span>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
